import { useState, useEffect } from 'react'
import { ArrowLeft, ArrowUpRight, Menu, X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { format } from 'date-fns'



interface JourneyPost {
  id: string
  title: string
  excerpt: string
  content?: string
  category: string
  published_at: string
  date?: string
}

export function BlogsPage() {
  const [posts, setPosts] = useState<JourneyPost[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPostId, setSelectedPostId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return new URLSearchParams(window.location.search).get('post')
  })
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false
    const stored = localStorage.getItem('theme')
    if (stored === 'dark') return true
    if (stored === 'light') return false
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const onPopState = () => {
      const postId = new URLSearchParams(window.location.search).get('post')
      setSelectedPostId(postId)
    }

    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    document.documentElement.classList.toggle('lp-dark', isDark)
    document.documentElement.classList.toggle('dark', isDark)
    localStorage.setItem('theme', isDark ? 'dark' : 'light')
    return () => {
      document.documentElement.classList.remove('lp-dark')
      // We don't remove 'dark' here because the user might navigate to the app
    }
  }, [isDark])

  useEffect(() => {
    async function fetchPosts() {
      try {
        const { data, error } = await supabase
          .from('journey_posts')
          .select('id, title, excerpt, content, category, published_at')
          .order('published_at', { ascending: false })

        if (error) throw error

        if (data && data.length > 0) {
          const formattedPosts = data.map((post: JourneyPost) => ({
            ...post,
            date: format(new Date(post.published_at), 'MMM d, yyyy'),
          }))
          setPosts(formattedPosts)
        }
      } catch (err) {
        console.error('Error fetching journey posts:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchPosts()
  }, [])

  const selectedPost = posts.find(post => post.id === selectedPostId) ?? null

  const openPost = (postId: string) => {
    const nextUrl = `${window.location.pathname}?post=${encodeURIComponent(postId)}`
    window.history.pushState({ postId }, '', nextUrl)
    setSelectedPostId(postId)
  }

  const closePost = () => {
    window.history.pushState({}, '', window.location.pathname)
    setSelectedPostId(null)
  }

  return (
    <div className={`lp${isDark ? ' lp--dark' : ''}`}>
      {/* ─── NAV ───────────────────────────────────────────────── */}
      <nav className={`lp-nav${scrolled ? ' lp-nav--scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <a href="/home" className="lp-wordmark flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            JOT
          </a>

          <div className="lp-nav-center">
            <a href="/home" className="lp-nav-link">Clipboard</a>
            <a href="/editor" className="lp-nav-link">Editor</a>
            <a href="/blogs" className="lp-nav-link font-medium text-[hsl(var(--color-text-primary))]">Journey</a>
          </div>

          <div className="lp-nav-right">
            <button className="lp-theme-toggle" onClick={() => setIsDark(d => !d)} aria-label="Toggle theme">
              <span className="lp-theme-track">
                <span className={`lp-theme-thumb${isDark ? ' lp-theme-thumb--on' : ''}`} />
              </span>
              <span className="lp-theme-label">{isDark ? 'Light' : 'Dark'}</span>
            </button>
            <a href="/" className="lp-open-btn">Open app <ArrowUpRight className="w-3.5 h-3.5" /></a>
            <button className="lp-hamburger" onClick={() => setMobileMenuOpen(o => !o)}>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lp-mobile-nav">
            <a href="/home" onClick={() => setMobileMenuOpen(false)}>Clipboard</a>
            <a href="/editor" onClick={() => setMobileMenuOpen(false)}>Editor</a>
            <a href="/blogs" onClick={() => setMobileMenuOpen(false)}>Journey</a>
            <a href="/" className="lp-mobile-cta">Open app →</a>
          </div>
        )}
      </nav>

      {/* ─── HEADER ────────────────────────────────────────────── */}
      {!selectedPost && (
        <section className="lp-hero" style={{ paddingBottom: '2rem', borderBottom: 'none' }}>
          <div className="lp-hero-inner" style={{ gridTemplateColumns: '1fr', maxWidth: '720px' }}>
            <div className="lp-hero-left" style={{ alignItems: 'flex-start', textAlign: 'left' }}>
              <p className="lp-hero-eyebrow" style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em' }}>~/logs/building-jot</p>
              <h1 className="lp-hero-headline" style={{ fontSize: 'clamp(3rem, 6vw, 4.5rem)', letterSpacing: '-0.05em' }}>
                Journey
              </h1>
              <p className="lp-hero-body" style={{ maxWidth: '100%', fontSize: '1.15rem', marginTop: '1rem' }}>
                A chronological log of building JOT. Thoughts, updates, and engineering notes.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ─── CONTENT ─────────────────────────────────────────── */}
      <section className="lp-section" style={{ paddingTop: selectedPost ? '6rem' : '0' }}>
        <div className="lp-section-inner" style={{ maxWidth: '720px' }}>
          {selectedPost ? (
            <article style={{ width: '100%' }}>
              <button 
                onClick={closePost}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.85rem',
                  color: 'hsl(var(--lp-text3))',
                  marginBottom: '3rem',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.color = 'hsl(var(--lp-text))'}
                onMouseOut={(e) => e.currentTarget.style.color = 'hsl(var(--lp-text3))'}
              >
                <ArrowLeft className="w-4 h-4" /> Back to logs
              </button>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '1rem',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.85rem',
                color: 'hsl(var(--lp-text3))',
                marginBottom: '1.5rem'
              }}>
                <span>{selectedPost.date}</span>
                <span style={{ color: 'hsl(var(--lp-border))' }}>—</span>
                <span style={{ color: 'hsl(var(--lp-gold-fg))', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{selectedPost.category}</span>
              </div>

              <h1 style={{ 
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 'clamp(2rem, 5vw, 3rem)', 
                fontWeight: 700, 
                color: 'hsl(var(--lp-text))',
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                marginBottom: '3rem'
              }}>
                {selectedPost.title}
              </h1>

              <div 
                className="prose prose-lg dark:prose-invert max-w-none"
                style={{ 
                  color: 'hsl(var(--lp-text2))',
                  lineHeight: 1.8,
                  fontSize: '1.1rem'
                }}
              >
                {selectedPost.content ? (
                  selectedPost.content.split('\n\n').map((paragraph: string, i: number) => (
                    <p key={i} style={{ marginBottom: '1.5em' }}>{paragraph}</p>
                  ))
                ) : (
                  <p>Content not available.</p>
                )}
              </div>
            </article>
          ) : loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '2rem', height: '2rem', border: '2px solid hsl(var(--lp-border))', borderTopColor: 'hsl(var(--lp-gold))', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
                <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem', color: 'hsl(var(--lp-text3))' }}>Loading entries...</p>
              </div>
            </div>
          ) : posts.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.95rem', color: 'hsl(var(--lp-text3))' }}>No entries yet. Check back soon.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem', width: '100%' }}>
              {posts.map((post, i) => (
                <article 
                  key={post.id} 
                  className="group"
                  onClick={() => openPost(post.id)}
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '0.75rem',
                    cursor: 'pointer',
                    paddingBottom: '3rem',
                    borderBottom: i !== posts.length - 1 ? '1px dashed hsl(var(--lp-border))' : 'none'
                  }}
                >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.8rem',
                  color: 'hsl(var(--lp-text3))'
                }}>
                  <span>{post.date}</span>
                  <span style={{ color: 'hsl(var(--lp-border))' }}>—</span>
                  <span style={{ color: 'hsl(var(--lp-gold-fg))', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{post.category}</span>
                </div>
                
                <h2 style={{ 
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '1.75rem', 
                  fontWeight: 700, 
                  color: 'hsl(var(--lp-text))',
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  transition: 'color 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.color = 'hsl(var(--lp-gold))'}
                onMouseOut={(e) => e.currentTarget.style.color = 'hsl(var(--lp-text))'}
                >
                  {post.title}
                </h2>
                
                <p style={{ 
                  fontSize: '1.05rem', 
                  color: 'hsl(var(--lp-text2))', 
                  lineHeight: 1.6,
                  maxWidth: '90%'
                }}>
                  {post.excerpt}
                </p>
                
                <div style={{ 
                  marginTop: '0.5rem',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: 'hsl(var(--lp-gold))',
                  opacity: 0,
                  transform: 'translateX(-10px)',
                  transition: 'all 0.2s ease'
                }}
                className="group-hover:opacity-100 group-hover:translate-x-0"
                >
                  Read entry <span style={{ display: 'inline-block', marginLeft: '4px' }}>→</span>
                </div>
              </article>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* ─── FOOTER ────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <span className="lp-footer-wordmark">JOT</span>
          <span className="lp-footer-copy">© {new Date().getFullYear()} JOT. All rights reserved.</span>
          <div className="lp-footer-links">
            <a href="/home" className="lp-footer-link">Clipboard</a>
            <a href="/editor" className="lp-footer-link">Editor</a>
            <a href="/" className="lp-footer-link">App</a>
            <a href="/blogs" className="lp-footer-link">Journey</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
